import HomeAction from 'home/actions/HomeAction'
import { isEqual } from 'lodash'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import UserAction from '../user/actions/UserAction'
import { BASIC_HOMEPAGE_VIEWS } from '../user/constants/UserConstants'
import UserViewDto from '../user/dto/UserViewDto'
import { Button, CardContent, Grid, Icon, Card } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'
import { darkBlue, grey } from 'utils/constants/ColorTheme'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'

const AdministrationHomePageApp = () => {
    const dispatch = useDispatch()

    const { genericUserView } = useSelector(store => ({
        genericUserView: store.UserReducer.genericUserView,
    }), shallowEqual)

    const [editMode, setEditMode] = useState(false)
    const [homepageViews, setHomePageViews] = useState([])

    useEffect(() => {
        dispatch(HomeAction.setHelpLink('administration', '91'))
        dispatch(UserAction.fetchGenericUserView())
    }, [])

    useTitle(() => {
        return [
            {
                title: i18n.administration,
                href: 'administration',
            }, {
                title: i18n.adminHomepage,
                href: 'administration/homepage',
            },
        ]
    }, [])

    const saveView = () => {
        if (!isEqual(genericUserView, homepageViews)) {
            dispatch(UserAction.updateGenericUserView(homepageViews, () => setEditMode(false)))
        } else {
            setEditMode(false)
        }
    }

    useActions(() =>
        editMode ? {
            cancel: () => {
                setEditMode(false)
                setHomePageViews(genericUserView)
            },
            save: saveView,
        } : {
            edit: () => setEditMode(true),
        }
    , [genericUserView, homepageViews])

    useEffect(() => {
        setHomePageViews(genericUserView)
    }, [genericUserView])

    const onChangeView = (view, changes) => {
        const newView = new UserViewDto({ ...view, ...changes })
        const userView = homepageViews.filter(v => v.panel != newView.panel)
        setHomePageViews([...userView, newView])
    }

    const getCard = (panel, size) => {
        const view = homepageViews.find(v => v.panel === panel) || { ...BASIC_HOMEPAGE_VIEWS.find(v => v.panel === panel), visible: false }
        const actions = []
        if (view && view.visible) {
            if (['quality', 'piezometry', 'hydrometry', 'pluviometry', 'productionUnit'].includes(panel)) {
                actions.push({
                    icon: view.graphicMode ? 'list' : 'insert_chart',
                    color: 'white',
                    onClick: () => {},
                })
            }
            actions.push({
                icon: 'visibility',
                color: 'white',
                onClick: () => editMode && onChangeView(view, { visible: false }),
            })
        } else {
            actions.push({
                icon: 'visibility_off',
                color: 'white',
                onClick: () => editMode && onChangeView(view, { visible: true }),
            })
        }
        return (
            <Card
                height={size}
                sx={{ mb: 2 }}
            >
                <CardTitle
                    title={i18n[panel]}
                    actions={actions}
                    color={editMode ? darkBlue : grey}
                />
                <CardContent
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {[
                        STATION_TYPE_NAME.quality,
                        STATION_TYPE_NAME.piezometry,
                        STATION_TYPE_NAME.hydrometry,
                        STATION_TYPE_NAME.pluviometry,
                        STATION_TYPE_NAME.productionUnit,
                    ].includes(panel) && (
                        <Icon fontSize='large'>
                            {view.graphicMode ? 'insert_chart' : 'list'}
                        </Icon>
                    )}
                </CardContent>
            </Card>
        )
    }

    const resetView = () => {
        setHomePageViews(BASIC_HOMEPAGE_VIEWS)
    }

    const bookmarks = [
        STATION_TYPE_NAME.quality,
        STATION_TYPE_NAME.piezometry,
        STATION_TYPE_NAME.hydrometry,
        STATION_TYPE_NAME.pluviometry,
        STATION_TYPE_NAME.productionUnit,
        STATION_TYPE_NAME.agriculture,
        STATION_TYPE_NAME.perimeters,
    ].map(type => getCard(type, 70))
    return (
        <Grid container >
            <Grid container size={12} spacing={1}>
                <Grid size={9} spacing={1}>
                    { bookmarks }
                    { getCard('map', 400) }
                </Grid>
                <Grid size={3} spacing={1}>
                    <Button
                        disabled={!editMode}
                        onClick={resetView}
                        sx={{ mb: 2 }}
                        variant='contained'
                        fullWidth
                    >
                        {i18n.reinit}
                    </Button>
                    { getCard('keyFigures', 100) }
                    { getCard('arrests', 150) }
                    { getCard('vigilances', 150) }
                    { getCard('tweets', 400) }
                </Grid>
            </Grid>
        </Grid>
    )
}

export default AdministrationHomePageApp
